import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Pagination from '../components/Pagination';
import usePagination from '../hooks/usePagination';
import api from '../services/api';
import { getHourCaps } from '../services/hourCapsApi';
import toast, { Toaster } from 'react-hot-toast';
import DatePicker, { registerLocale } from 'react-datepicker';
import { ptBR } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import {
  LayersIcon,
  UpdateIcon,
  MagicWandIcon,
  EraserIcon,
  CheckCircledIcon,
  PersonIcon,
  ClockIcon,
  StopwatchIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
} from '@radix-ui/react-icons';
import './Global.css';
import './BulkTimeRecords.css';

registerLocale('pt-BR', ptBR);

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
// Leitor de tela lê "20 de agosto"; a abreviação da grade viraria "20 de ago".
const MESES_EXTENSO = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];
// Domingo → sábado, na ordem de Date.getDay().
const INICIAIS_SEMANA = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
const NOMES_SEMANA = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];

const ESTADO_TEXTO = {
  livre: 'livre',
  fds: 'fim de semana',
  lancado: 'já lançado',
  impedido: 'impedido',
  glosado: 'glosado pela CGP',
  'pendente-cgp': 'pendência na CGP',
};

/* Códigos que a CGP devolve no lugar do motivo. Ela manda a chave crua
   (`aula_conflito_cod`); a tela precisa dizer o que isso significa. */
const MOTIVOS_CGP = {
  aula_conflito_cod: 'conflito com a escala do COD (estava de serviço no horário)',
  aula_em_ferias: 'militar de férias na data',
  aula_em_afastamento: 'militar afastado na data',
  aula_duplicada: 'aula duplicada',
  aula_sem_registro: 'sem registro correspondente',
};

/* O hint do efetivo é montado no render: o teto vem da config (tela Limites),
   não é mais número fixo. */
const ABAS = [
  { id: 'cmcb', label: 'Efetivo do CMCB', hint: null },
  { id: 'instrutores', label: 'Instrutores (por horas)', hint: 'lista autorizada no cadastro' },
];

/** Valor mostrado enquanto a config não chega (ou se a rota falhar). */
const TETO_COLEGIO_PADRAO = 48;

const BLOCOS = [4, 6];

/** Até esse tamanho a aba de instrutores mostra a lista inteira sem busca. */
const LIMITE_LISTA_INTEIRA = 12;

/* O backend devolve o motivo em snake_case (é chave, não texto de tela); o
   badge tem text-transform: uppercase e mostrava "NAO_AUTORIZADO". */
const MOTIVOS = {
  duplicado: 'duplicado',
  impedimento: 'impedimento',
  erro: 'erro',
  nao_autorizado: 'não autorizado',
};

/** Turno parcial é cortado em múltiplos de 5 min — 2,5h vira 08:00→10:30. */
const PASSO_MIN = 5;
/** Sobra menor que um passo não vira turno; serve de tolerância nas contas. */
const SOBRA_MIN = PASSO_MIN / 60;
const EPS = 0.001;

const pad = (n) => String(n).padStart(2, '0');

const toISO = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const fromISO = (iso) => {
  const [ano, mes, dia] = String(iso).split('-').map(Number);
  return new Date(ano, mes - 1, dia);
};

const formatBR = (iso) => {
  const [ano, mes, dia] = String(iso).split('-');
  return `${dia}/${mes}/${ano}`;
};

const formatHoras = (valor) => {
  const num = Number(valor) || 0;
  const horas = Math.floor(num + 1e-9);
  const minutos = Math.round((num - horas) * 60);
  return minutos === 0 ? `${horas}h` : `${horas}h${pad(minutos)}`;
};

const formatMatricula = (valor) => {
  if (!valor) return '';
  const texto = String(valor);
  if (texto.includes('.')) return texto;
  const limpo = texto.toUpperCase().replace(/[^0-9X]/g, '');
  if (limpo.length < 8) return texto;
  return `${limpo.slice(0, 3)}.${limpo.slice(3, 6)}-${limpo.slice(6, 7)}-${limpo.slice(7, 8)}`;
};

const minutosDe = (hhmm) => {
  const [h, m] = String(hhmm || '').split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return NaN;
  return h * 60 + m;
};

/* ------------------------------------------------------------------ */
/* Seleção — o modelo guarda o TURNO de cada célula, não só o dia:      */
/*   { [userId]: { [date]: { entryTime, exitTime } } }                  */
/* É o que permite fechar uma cota que só tem 4h de folga com um dia    */
/* curto (08:00→12:00) em vez de deixar a pessoa 4h abaixo do teto.     */
/* ------------------------------------------------------------------ */

const horasDoTurno = (turno) => {
  if (!turno) return 0;
  const ini = minutosDe(turno.entryTime);
  const fim = minutosDe(turno.exitTime);
  if (!Number.isFinite(ini) || !Number.isFinite(fim)) return 0;
  return Math.max(0, (fim - ini) / 60);
};

const diasDe = (mapa) => (mapa ? Object.keys(mapa).length : 0);

const horasDe = (mapa) =>
  Object.values(mapa || {}).reduce((soma, turno) => soma + horasDoTurno(turno), 0);

/** Turno que começa em `entrada` e dura `horas`, cortado para baixo em passos
 *  de 5 min — cortar para baixo é o que garante que a cota nunca é estourada. */
const turnoDe = (entrada, horas) => {
  const inicio = minutosDe(entrada);
  const minutos = Math.floor((horas * 60 + 1e-6) / PASSO_MIN) * PASSO_MIN;
  if (!Number.isFinite(inicio) || minutos <= 0) return null;
  const fim = inicio + minutos;
  if (fim >= 24 * 60) return null; // turno desta tela não vira a meia-noite
  return { entryTime: entrada, exitTime: `${pad(Math.floor(fim / 60))}:${pad(fim % 60)}` };
};

/**
 * Espalha `horas` pelos `dias` em ordem cronológica: dias cheios com o turno
 * padrão enquanto couber e um último dia menor para fechar a conta exata
 * (14h em blocos de 4h = 4 + 4 + 4 + 2). Devolve o que não coube.
 */
const distribuirEmDias = (dias, horas, padrao, horasPadrao) => {
  const marcados = {};
  let restante = horas;
  for (const date of dias) {
    if (restante < SOBRA_MIN - EPS) break;
    if (restante >= horasPadrao - EPS) {
      // Dia cheio reusa o turno padrão em vez de recalcular: assim 08:07→16:00
      // não vira 08:07→15:57 por causa do arredondamento de 5 min.
      marcados[date] = padrao;
      restante -= horasPadrao;
      continue;
    }
    const turno = turnoDe(padrao.entryTime, restante);
    if (!turno) break;
    marcados[date] = turno;
    restante -= horasDoTurno(turno);
  }
  return { marcados, sobra: Math.max(0, restante) };
};

/**
 * Ciclo de pagamento: dia 16 até o dia 15 do mês seguinte. `deslocamento`
 * anda ciclos inteiros para trás (-1 = ciclo anterior).
 */
const cicloDe = (referencia, deslocamento = 0) => {
  const base = new Date(referencia.getFullYear(), referencia.getMonth(), 1);
  // Antes do dia 16 ainda estamos no ciclo que abriu no mês passado.
  if (referencia.getDate() <= 15) base.setMonth(base.getMonth() - 1);
  base.setMonth(base.getMonth() + deslocamento);
  return {
    start: toISO(new Date(base.getFullYear(), base.getMonth(), 16)),
    end: toISO(new Date(base.getFullYear(), base.getMonth() + 1, 15)),
  };
};

const mensagemErro = (erro, padraoMsg) =>
  erro?.response?.data?.message || erro?.message || padraoMsg;

const SELECIONAVEIS = ['livre', 'fds'];
const ehSelecionavel = (estado) => SELECIONAVEIS.includes(estado);

/* Toda célula de dia carrega uid+date (o foco do teclado precisa alcançar até
   as bloqueadas); só as marcáveis ganham data-sel — é ele que separa "célula
   da grade" de "célula que o clique/arrasto pode pintar". */
const SELETOR_CELULA = '[data-uid][data-date]';
const SELETOR_MARCAVEL = '[data-uid][data-date][data-sel]';
/* Campo dentro da coluna do militar (horas a lançar): o clique nele não pode
   virar "alternar a linha inteira". */
const SELETOR_CAMPO = 'input, select, textarea, button';

/** O bulk/check aceita no máximo 200 militares por chamada — a lista de
 *  instrutores passa disso, então vai em lotes e as respostas são coladas. */
const LOTE_CHECK = 200;

const verificarEmLotes = async (userIds, startDate, endDate) => {
  let acumulado = null;
  for (let i = 0; i < userIds.length; i += LOTE_CHECK) {
    const parte = userIds.slice(i, i + LOTE_CHECK);
    const { data } = await api.post('/time-records/bulk/check', {
      userIds: parte,
      startDate,
      endDate,
    });
    acumulado = acumulado
      ? {
          ...acumulado,
          usuarios: [...acumulado.usuarios, ...(data.usuarios || [])],
          existentes: [...acumulado.existentes, ...(data.existentes || [])],
          impedidos: [...acumulado.impedidos, ...(data.impedidos || [])],
        }
      : data;
  }
  if (acumulado && userIds.length > LOTE_CHECK) {
    acumulado.usuarios = [...acumulado.usuarios].sort((a, b) => a.name.localeCompare(b.name));
  }
  return acumulado;
};

/* ------------------------------------------------------------------ */
/* Linha da grade — memoizada para que pintar uma linha não redesenhe   */
/* as outras 40. Só muda quando a seleção daquela pessoa muda.          */
/* ------------------------------------------------------------------ */
const LinhaMilitar = React.memo(function LinhaMilitar({
  row,
  tetosAtivos = true,
  sel,
  horasPadrao,
  meta,
  modoPedido,
  pedido,
  pedidoTexto,
  aoMudarPedido,
  focoDate,
  aviso,
}) {
  const militar = row.user;
  const marcados = diasDe(sel);
  // Sugestão para o campo de horas: o que falta para a cota do ciclo.
  const faltaParaCota = Math.max(
    0,
    (Number(militar.cota) || 0) - (Number(militar.horasNoPeriodo) || 0),
  );
  const horasSelecionadas = horasDe(sel);

  const resumo = useMemo(() => {
    const lancado = Number(militar.horasNoPeriodo) || 0;
    const total = lancado + horasSelecionadas;
    const excedente = meta > 0 ? total - meta : 0;

    // Teto do ciclo que vale para a pessoa (`teto` do bulk/check: categoria
    // ou individual, limitado ao geral) — não a meta (`cota`), que no
    // instrutor é a carga pedida e fica abaixo do teto. Passar do teto não
    // dá erro nenhum na hora de gravar — o excedente fica retido no
    // registro, calado. Por isso a linha avisa aqui, antes de o admin lançar.
    const teto = Number(militar.teto) || Number(militar.cota) || 0;
    const acimaDoTeto = teto > 0 && total - teto > EPS;

    let estado = 'parcial';
    let nota = meta > 0 ? `faltam ${formatHoras(meta - total)}` : `${formatHoras(total)} no período`;

    // Na aba dos instrutores a nota fala do PEDIDO (o que o admin digitou),
    // porque é ele que a distribuição persegue; a barra continua medindo o
    // teto, igual à aba do efetivo.
    if (modoPedido) {
      const restante = pedido - horasSelecionadas;
      if (pedido <= 0) {
        nota = 'sem horas pedidas';
      } else if (Math.abs(restante) < EPS) {
        estado = 'completa';
        nota = 'horas pedidas fechadas';
      } else if (restante < -EPS) {
        estado = 'excedida';
        nota = `ultrapassa o pedido em ${formatHoras(-restante)}`;
      } else {
        nota = `faltam ${formatHoras(restante)} do pedido`;
      }
    } else if (meta > 0 && Math.abs(excedente) < EPS) {
      estado = 'completa';
      nota = 'cota completa';
    }

    // O teto manda sobre qualquer outra leitura: passar dele não trava o
    // lançamento — o excedente fica retido no registro, depois e em silêncio.
    if (acimaDoTeto) {
      estado = 'excedida';
      nota = tetosAtivos
        ? `ultrapassa o teto de ${formatHoras(teto)} — o excedente fica retido no registro`
        : `ultrapassa o teto de ${formatHoras(teto)} — tetos desligados: vai inteiro para a CGP`;
    }

    const base = meta > 0 ? meta : Math.max(total, 1);
    const limite = Math.max(base, total);
    return {
      total,
      estado,
      nota,
      pctLancado: Math.min(100, (lancado / limite) * 100),
      pctAdicionado: Math.min(100, (horasSelecionadas / limite) * 100),
    };
  }, [militar.horasNoPeriodo, militar.teto, militar.cota, horasSelecionadas, meta, modoPedido, pedido, tetosAtivos]);

  return (
    <tr role="row" className={`bulk-row ${marcados > 0 ? 'is-touched' : ''}`}>
      <th
        scope="row"
        className="bulk-name"
        data-rowtoggle={militar.id}
        title={`${militar.name} — clique no nome para alternar a linha inteira`}
      >
        <span className="bulk-name-body">
          <span className="bulk-name-label">{militar.name}</span>
          <span className="bulk-name-meta">
            {militar.militaryRank || 'Militar'}
            {militar.matricula ? ` · ${formatMatricula(militar.matricula)}` : ''}
          </span>
          {aviso && (
            <span className="bulk-aviso" title={aviso.message}>
              ⚠ afastamento não bloqueante
            </span>
          )}
        </span>
        {modoPedido && (
          <span className="bulk-pedido">
            <input
              type="number"
              className="bulk-pedido-input"
              min="0"
              step="0.5"
              inputMode="decimal"
              value={pedidoTexto}
              onChange={(e) => aoMudarPedido(militar.id, e.target.value)}
              aria-label={`Horas a lançar para ${militar.name}`}
              title="Horas a lançar neste período (aceita meia hora)"
            />
            <span className="bulk-pedido-label">horas</span>
            {!pedidoTexto && faltaParaCota > 0 && (
              <button
                type="button"
                className="bulk-pedido-sugestao"
                onClick={() => aoMudarPedido(militar.id, String(faltaParaCota))}
                title={`Usar o que falta para a cota de ${formatHoras(militar.cota)}`}
              >
                faltam {formatHoras(faltaParaCota)}
              </button>
            )}
          </span>
        )}
      </th>

      {row.cells.map((cell) => {
        const turno = sel ? sel[cell.date] : null;
        const marcada = !!turno;
        const horas = marcada ? horasDoTurno(turno) : 0;
        // Dia curto mostra o número na célula: sem isso o admin não enxerga
        // que aquele dia entra com menos horas que o padrão do lote.
        const parcial = marcada && Math.abs(horas - horasPadrao) > EPS;
        const livre = ehSelecionavel(cell.estado);
        return (
          <td
            key={cell.date}
            role="gridcell"
            // Roving tabIndex: a grade inteira é UMA parada de Tab e as setas
            // fazem o resto. `focoDate` só chega na linha que está com o foco,
            // então mover o foco redesenha 2 linhas, não as 25 da página.
            tabIndex={cell.date === focoDate ? 0 : -1}
            aria-label={`${cell.rotulo}, ${
              marcada
                ? `selecionado, ${turno.entryTime} às ${turno.exitTime}, ${formatHoras(horas)}`
                : ESTADO_TEXTO[cell.estado]
            }`}
            aria-selected={livre ? marcada : undefined}
            aria-disabled={livre ? undefined : true}
            className={
              `bulk-cell is-${cell.estado}` +
              (marcada ? ' is-selected' : '') +
              (parcial ? ' is-parcial' : '') +
              (cell.isToday ? ' is-today' : '') +
              (cell.isMonthStart ? ' is-month-start' : '')
            }
            title={
              marcada
                ? `${cell.legenda} — selecionado: ${turno.entryTime} às ${turno.exitTime} · ${formatHoras(horas)}`
                : cell.titulo
            }
            data-uid={militar.id}
            data-date={cell.date}
            data-sel={livre ? (marcada ? '1' : '0') : undefined}
          >
            {marcada && parcial ? <span className="bulk-part">{formatHoras(horas)}</span> : null}
            {marcada && !parcial ? <span className="bulk-tick">✓</span> : null}
            {cell.estado === 'lancado' ? <span className="bulk-tick is-done">✓</span> : null}
          </td>
        );
      })}

      <td className={`bulk-total is-${resumo.estado}`}>
        <span className="bulk-total-nums">
          <strong>{formatHoras(resumo.total)}</strong>
          <span className="bulk-total-cota">/ {meta > 0 ? formatHoras(meta) : '—'}</span>
        </span>
        <span className="bulk-bar">
          <span className="bulk-bar-fill" style={{ width: `${resumo.pctLancado}%` }} />
          <span className="bulk-bar-add" style={{ width: `${resumo.pctAdicionado}%` }} />
        </span>
        <span className="bulk-total-nota">{resumo.nota}</span>
      </td>
    </tr>
  );
});

/* ------------------------------------------------------------------ */

const BulkTimeRecords = () => {
  const cicloInicial = useMemo(() => cicloDe(new Date(), 0), []);

  const [aba, setAba] = useState('cmcb');
  const ehInstrutores = aba === 'instrutores';

  const [inicio, setInicio] = useState(cicloInicial.start);
  const [fim, setFim] = useState(cicloInicial.end);
  const [modoPeriodo, setModoPeriodo] = useState('atual');

  const [entryTime, setEntryTime] = useState('08:00');
  const [exitTime, setExitTime] = useState('16:00');

  // Aba dos instrutores: blocos de 4h ou 6h a partir de uma hora de início.
  const [blocoHoras, setBlocoHoras] = useState(4);
  const [blocoInicio, setBlocoInicio] = useState('08:00');

  const [dados, setDados] = useState(null);
  // Teto do efetivo do colégio (config da tela Limites): só rótulo aqui — a
  // cota por pessoa continua vindo do backend em bulk/check.
  const [tetoColegio, setTetoColegio] = useState(TETO_COLEGIO_PADRAO);
  const [efetivoInativos, setEfetivoInativos] = useState(0);
  const [instrutoresTotal, setInstrutoresTotal] = useState(0);
  const [erroCarga, setErroCarga] = useState('');
  const [loading, setLoading] = useState(true);
  const [verificando, setVerificando] = useState(false);
  const [lancando, setLancando] = useState(false);

  const [selecao, setSelecao] = useState({});
  const [pedidos, setPedidos] = useState({});
  const [mostrarTodos, setMostrarTodos] = useState(false);
  const [sobras, setSobras] = useState([]);
  const [busca, setBusca] = useState('');
  const [observacao, setObservacao] = useState('');
  const [confirmando, setConfirmando] = useState(false);
  const [resultado, setResultado] = useState(null);

  // Pintura por arrasto: guarda se o gesto está ativo e se ele marca ou desmarca.
  const arrasto = useRef({ ativo: false, marcar: true });

  // Célula que detém o foco da grade (roving tabIndex) e o container rolável.
  const [foco, setFoco] = useState(null);
  const gradeRef = useRef(null);

  // Última linha mexida — é dela que o aria-live fala o total.
  const [anuncio, setAnuncio] = useState('');
  const linhaTocada = useRef(null);

  // Turno que o clique, o arrasto e o teclado pintam. Na aba do efetivo é o
  // horário do lote; na dos instrutores é o bloco escolhido.
  const turnoPadrao = useMemo(() => {
    if (!ehInstrutores) return { entryTime, exitTime };
    return turnoDe(blocoInicio, blocoHoras) || { entryTime: blocoInicio, exitTime: blocoInicio };
  }, [ehInstrutores, entryTime, exitTime, blocoInicio, blocoHoras]);

  const horasPorDia = useMemo(() => horasDoTurno(turnoPadrao), [turnoPadrao]);

  // Cada carga recebe um número; resposta de pedido antigo é descartada. Sem
  // isso, digitar a data no DatePicker dispara várias cargas e a grade pode
  // acabar pintada com a resposta mais lenta, de um período que não é o atual.
  const cargaSeq = useRef(0);

  const carregar = useCallback(
    async (modo = 'full') => {
      const seq = ++cargaSeq.current;
      if (modo === 'full') setLoading(true);
      else setVerificando(true);
      try {
        const { data: todos } = await api.get('/users');
        if (seq !== cargaSeq.current) return;
        const lista = Array.isArray(todos) ? todos : [];
        const efetivo = lista.filter((u) => u.efetivoCmcb);
        const efetivoAtivo = efetivo.filter((u) => u.isActive);
        // Instrutor autorizado: quem está marcado como tal no cadastro, e
        // mais ninguém. O filtro antigo era "ativo, fora do efetivo e com
        // horasAulas", o que trazia dezenas de militares que ninguém pediu
        // para lançar — um clique errado distribuía dias para o instrutor
        // errado. O backend recusa quem não está marcado (User.instrutorLote).
        //
        // Marcado nas duas listas continua sendo do EFETIVO e só: as abas são
        // disjuntas de propósito, senão a mesma pessoa apareceria duas vezes
        // com duas contas de cota diferentes na mesma tela.
        const instrutores = lista.filter(
          (u) => u.instrutorLote && !u.efetivoCmcb && u.isActive
        );
        setEfetivoInativos(efetivo.length - efetivoAtivo.length);
        setInstrutoresTotal(instrutores.length);
        setErroCarga('');

        const alvo = ehInstrutores ? instrutores : efetivoAtivo;
        if (alvo.length === 0) {
          setDados(null);
          return;
        }

        const check = await verificarEmLotes(
          alvo.map((u) => u.id),
          inicio,
          fim
        );
        if (seq !== cargaSeq.current) return;
        setDados(check);
        if (modo === 'check') toast.success('Grade atualizada');
      } catch (erro) {
        if (seq !== cargaSeq.current) return;
        const msg = mensagemErro(erro, 'Não foi possível carregar o período');
        setErroCarga(msg);
        toast.error(msg);
      } finally {
        if (seq === cargaSeq.current) {
          setLoading(false);
          setVerificando(false);
        }
      }
    },
    [inicio, fim, ehInstrutores]
  );

  useEffect(() => {
    // 350ms: o DatePicker aceita digitação e emite onChange a cada texto que
    // parseia — sem espera, cada tecla viraria um GET /users + bulk/check.
    const timer = setTimeout(() => carregar('full'), 350);
    return () => clearTimeout(timer);
  }, [carregar]);

  useEffect(() => {
    let vivo = true;
    getHourCaps()
      .then((cfg) => {
        if (vivo && Number.isFinite(Number(cfg?.tetoColegio))) setTetoColegio(Number(cfg.tetoColegio));
      })
      .catch(() => {
        // Sem config a tela segue com o padrão: é só rótulo.
      });
    return () => { vivo = false; };
  }, []);

  // Trocar de período invalida tudo que estava pintado.
  useEffect(() => {
    setSelecao((atual) => (Object.keys(atual).length > 0 ? {} : atual));
    setSobras([]);
  }, [inicio, fim]);

  // As duas abas trabalham listas de pessoas diferentes; carregar a outra com
  // seleção pendente esconderia da tela um lote pronto para gravar.
  useEffect(() => {
    setSelecao({});
    setPedidos({});
    setSobras([]);
    setBusca('');
  }, [aba]);

  useEffect(() => {
    const soltar = () => {
      arrasto.current.ativo = false;
    };
    window.addEventListener('mouseup', soltar);
    return () => window.removeEventListener('mouseup', soltar);
  }, []);

  // Mudar o horário do lote (ou o tamanho do bloco) reescreve as células que
  // estavam com o turno padrão anterior. As parciais, criadas para fechar uma
  // conta exata, ficam como estão — recalculá-las quebraria o fechamento.
  const padraoAnterior = useRef(turnoPadrao);
  useEffect(() => {
    const antes = padraoAnterior.current;
    padraoAnterior.current = turnoPadrao;
    if (antes.entryTime === turnoPadrao.entryTime && antes.exitTime === turnoPadrao.exitTime) {
      return;
    }
    setSelecao((anterior) => {
      let mudou = false;
      const proximo = {};
      Object.entries(anterior).forEach(([uid, mapa]) => {
        const novo = {};
        Object.entries(mapa).forEach(([date, turno]) => {
          const eraPadrao =
            turno.entryTime === antes.entryTime && turno.exitTime === antes.exitTime;
          if (eraPadrao) mudou = true;
          novo[date] = eraPadrao ? turnoPadrao : turno;
        });
        proximo[uid] = novo;
      });
      return mudou ? proximo : anterior;
    });
  }, [turnoPadrao]);

  const dias = useMemo(() => {
    const lista = dados?.periodo?.dias || [];
    const hoje = toISO(new Date());
    return lista.map((date) => {
      const d = fromISO(date);
      const dow = d.getDay();
      return {
        date,
        dia: d.getDate(),
        dow,
        inicial: INICIAIS_SEMANA[dow],
        isWeekend: dow === 0 || dow === 6,
        isToday: date === hoje,
        isMonthStart: d.getDate() === 1,
        legenda: `${NOMES_SEMANA[dow]}, ${formatBR(date)}`,
        porExtenso: `${d.getDate()} de ${MESES_EXTENSO[d.getMonth()]}`,
      };
    });
  }, [dados]);

  const gruposMes = useMemo(() => {
    const grupos = [];
    dias.forEach((d) => {
      const data = fromISO(d.date);
      const chave = `${data.getFullYear()}-${data.getMonth()}`;
      const ultimo = grupos[grupos.length - 1];
      if (ultimo && ultimo.chave === chave) ultimo.qtd += 1;
      else grupos.push({ chave, qtd: 1, label: `${MESES[data.getMonth()]}/${data.getFullYear()}` });
    });
    return grupos;
  }, [dias]);

  // Avisos que não bloqueiam (hoje: afastamento marcado como "não afasta").
  // Chegam do bulk/check porque a regra de quem afasta é do backend.
  const avisosPorMilitar = useMemo(() => {
    const mapa = new Map();
    (dados?.avisos || []).forEach((a) => {
      if (!mapa.has(a.userId)) mapa.set(a.userId, a);
    });
    return mapa;
  }, [dados]);

  const rows = useMemo(() => {
    if (!dados) return [];
    const lancados = new Map();
    (dados.existentes || []).forEach((r) => lancados.set(`${r.userId}|${r.date}`, r));
    const impedidos = new Map();
    (dados.impedidos || []).forEach((b) => impedidos.set(`${b.userId}|${b.date}`, b));

    return (dados.usuarios || []).map((militar) => {
      const cells = dias.map((d) => {
        const comum = {
          date: d.date,
          isToday: d.isToday,
          isMonthStart: d.isMonthStart,
          legenda: d.legenda,
          // Metade fixa do aria-label; o estado é colado na hora do render
          // porque muda a cada clique.
          rotulo: `${militar.name}, ${d.porExtenso}`,
        };
        const registro = lancados.get(`${militar.id}|${d.date}`);
        if (registro) {
          const entrada = String(registro.entryTime || '').substring(0, 5) || '--:--';
          const saida = String(registro.exitTime || '').substring(0, 5) || 'em aberto';
          const base = `${d.legenda} — já lançado: ${entrada} às ${saida} · ${formatHoras(registro.totalHours)}`;
          // Dia que a CGP recusou não pode aparecer como "resolvido": a hora
          // não vai ser paga, e até agora isso só existia na tela dela.
          if (registro.cgpGlosada) {
            return {
              ...comum,
              estado: 'glosado',
              titulo: `${base}\nGLOSADO pela CGP: ${MOTIVOS_CGP[registro.cgpMotivo] || registro.cgpMotivo || 'sem motivo informado'}`,
            };
          }
          if (registro.cgpPendencia) {
            return {
              ...comum,
              estado: 'pendente-cgp',
              titulo: `${base}\nPendência na CGP: ${MOTIVOS_CGP[registro.cgpPendencia] || registro.cgpPendencia}`,
            };
          }
          return { ...comum, estado: 'lancado', titulo: base };
        }
        const bloqueio = impedidos.get(`${militar.id}|${d.date}`);
        if (bloqueio) {
          return {
            ...comum,
            estado: 'impedido',
            titulo: `${d.legenda} — ${bloqueio.message || 'impedimento'}`,
          };
        }
        return {
          ...comum,
          estado: d.isWeekend ? 'fds' : 'livre',
          titulo: d.isWeekend
            ? `${d.legenda} — fim de semana (fora do preenchimento automático)`
            : `${d.legenda} — livre`,
        };
      });

      return {
        user: militar,
        cells,
        // Dias úteis sem registro e sem impedimento — a base do preenchimento automático.
        uteisLivres: cells.filter((c) => c.estado === 'livre').map((c) => c.date),
      };
    });
  }, [dados, dias]);

  // Um dia selecionado pode ter virado registro/impedimento entre uma verificação e outra.
  useEffect(() => {
    if (rows.length === 0) return;
    setSelecao((anterior) => {
      const ids = Object.keys(anterior);
      if (ids.length === 0) return anterior;
      let mudou = false;
      const proximo = {};
      rows.forEach((row) => {
        const atual = anterior[row.user.id];
        if (!atual || diasDe(atual) === 0) return;
        const validos = {};
        row.cells.forEach((c) => {
          if (ehSelecionavel(c.estado) && atual[c.date]) validos[c.date] = atual[c.date];
        });
        if (diasDe(validos) !== diasDe(atual)) mudou = true;
        proximo[row.user.id] = validos;
      });
      if (ids.some((id) => !(id in proximo) && diasDe(anterior[id]) > 0)) mudou = true;
      return mudou ? proximo : anterior;
    });
  }, [rows]);

  /* ------------------------ horas pedidas (instrutores) ------------------------ */

  /**
   * O campo de horas nasce VAZIO de propósito: aqui são dezenas de
   * instrutores, e pré-preencher todo mundo com "o que falta para a cota"
   * fazia "Distribuir horas" lançar para a lista inteira de uma vez — numa
   * verificação isso criou registros para um instrutor que ninguém tinha
   * pedido. Quem decide quem entra é o admin, digitando (ou clicando no
   * atalho "faltam Xh" que cada linha mostra).
   */
  const textoPedido = (row) => pedidos[row.user.id] ?? '';

  const pedidoDe = useCallback(
    (row) => {
      const bruto = pedidos[row.user.id];
      if (bruto === undefined || bruto === '') return 0;
      const numero = Number(String(bruto).replace(',', '.'));
      return Number.isFinite(numero) && numero > 0 ? numero : 0;
    },
    [pedidos]
  );

  // O teto da linha é o mesmo nas duas abas: o teto do colégio (config) que o
  // backend devolve em `cota`. No efetivo é meta (preencher até fechar); no
  // instrutor é limite (a direção decide quanto lançar). A barra media o
  // "lançado + pedido" na aba dos instrutores e por isso vivia em 100% —
  // dizia que estava tudo certo mesmo com o pedido acima do teto.
  const metaDe = useCallback((row) => Number(row.user.cota) || 0, []);

  const aoMudarPedido = useCallback((uid, valor) => {
    setPedidos((atual) => ({ ...atual, [uid]: valor }));
  }, []);

  /* ------------------------ quem aparece na grade ------------------------ */

  const linhasVisiveis = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return rows;
    const semAcento = (t) =>
      String(t || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
    const alvo = semAcento(termo);
    return rows.filter(
      (r) =>
        semAcento(r.user.name).includes(alvo) ||
        semAcento(r.user.matricula).includes(alvo) ||
        semAcento(r.user.militaryRank).includes(alvo)
    );
  }, [rows, busca]);

  // Lista nominal curta cabe inteira na tela: esconder linha aqui só faria o
  // admin procurar quem ele já sabe que está autorizado. O recolhimento (só
  // quem já tem dia marcado ou horas digitadas) fica para o caso de a
  // autorização crescer muito — e o "mostrar todos" continua sendo a saída.
  const listaCurta = rows.length <= LIMITE_LISTA_INTEIRA;
  const linhasListadas = useMemo(() => {
    if (!ehInstrutores || mostrarTodos || busca.trim() || listaCurta) return linhasVisiveis;
    return linhasVisiveis.filter(
      (r) => diasDe(selecao[r.user.id]) > 0 || pedidos[r.user.id] !== undefined
    );
  }, [ehInstrutores, mostrarTodos, busca, listaCurta, linhasVisiveis, selecao, pedidos]);

  // A grade é larga (um dia = uma coluna); paginar as LINHAS mantém o DOM
  // pequeno sem mexer no alcance das ações em lote, que continuam valendo
  // para todas as linhas listadas — não só as da página.
  const gradePaginacao = usePagination(linhasListadas, 25);
  const { setPage: setGradePage, pageItems: linhasPagina } = gradePaginacao;

  useEffect(() => {
    setGradePage(1);
  }, [busca, aba, mostrarTodos, setGradePage]);

  // Filtro, paginação e recarga trocam as linhas debaixo do foco. Quando a
  // célula guardada some da grade, a primeira da página assume o tabIndex=0
  // para que a grade nunca fique fora do alcance do Tab.
  const focoAtual = useMemo(() => {
    if (linhasPagina.length === 0 || dias.length === 0) return null;
    const continuaNaGrade =
      foco &&
      linhasPagina.some((r) => r.user.id === foco.uid) &&
      dias.some((d) => d.date === foco.date);
    return continuaNaGrade ? foco : { uid: linhasPagina[0].user.id, date: dias[0].date };
  }, [foco, linhasPagina, dias]);

  const totais = useMemo(() => {
    // Os números de meta olham só quem está listado (na aba dos instrutores a
    // grade mostra um recorte); os de seleção olham TODAS as linhas da aba,
    // senão o rodapé mostraria menos dias do que o lote realmente grava.
    const base = ehInstrutores ? linhasListadas : rows;
    let lancadas = 0;
    let faltam = 0;
    let pedidas = 0;
    let diasSelecionados = 0;
    let horasSelecionadas = 0;
    base.forEach((row) => {
      const lancado = Number(row.user.horasNoPeriodo) || 0;
      lancadas += lancado;
      const selecionadas = horasDe(selecao[row.user.id]);
      if (ehInstrutores) {
        // O que ainda falta distribuir é medido contra o que o admin PEDIU —
        // contra o teto do ciclo daria sempre um número enorme, já que o
        // instrutor normalmente recebe menos que o teto.
        const pedido = pedidoDe(row);
        pedidas += pedido;
        faltam += Math.max(0, pedido - selecionadas);
        return;
      }
      const meta = metaDe(row);
      if (meta > 0) faltam += Math.max(0, meta - lancado - selecionadas);
    });
    rows.forEach((row) => {
      diasSelecionados += diasDe(selecao[row.user.id]);
      horasSelecionadas += horasDe(selecao[row.user.id]);
    });
    return {
      pessoas: base.length,
      lancadas,
      faltam,
      pedidas,
      diasSelecionados,
      horasSelecionadas,
    };
  }, [ehInstrutores, linhasListadas, rows, selecao, pedidoDe, metaDe]);

  const cotaComum = useMemo(() => {
    const cotas = new Set(rows.map((r) => Number(r.user.cota) || 0));
    return cotas.size === 1 ? [...cotas][0] : null;
  }, [rows]);

  // Sem enxergar a coluna de total, a única pista de que a marcação surtiu
  // efeito é este texto. Roda depois do commit da seleção, então já lê o valor
  // novo; repetir a mesma frase não re-renderiza (React descarta o setState).
  useEffect(() => {
    const uid = linhaTocada.current;
    if (!uid) return;
    const row = rows.find((r) => r.user.id === uid);
    if (!row) return;
    const total = (Number(row.user.horasNoPeriodo) || 0) + horasDe(selecao[uid]);
    const meta = metaDe(row);
    setAnuncio(`${row.user.name}: ${formatHoras(total)}${meta > 0 ? ` de ${formatHoras(meta)}` : ''}`);
  }, [selecao, rows, metaDe]);

  /* ------------------------ seleção ------------------------ */

  const pintarCelula = useCallback(
    (uid, date, marcar) => {
      setSelecao((anterior) => {
        const atual = anterior[uid];
        const jaTem = !!(atual && atual[date]);
        if (jaTem === marcar) return anterior; // evita re-render durante o arrasto
        const proximo = { ...(atual || {}) };
        if (marcar) proximo[date] = turnoPadrao;
        else delete proximo[date];
        return { ...anterior, [uid]: proximo };
      });
    },
    [turnoPadrao]
  );

  const alternarCelula = (uid, date) => {
    const cell = rows.find((r) => r.user.id === uid)?.cells.find((c) => c.date === date);
    if (!cell || !ehSelecionavel(cell.estado)) return;
    linhaTocada.current = uid;
    pintarCelula(uid, date, !selecao[uid]?.[date]);
  };

  const alternarLinha = (uid) => {
    linhaTocada.current = uid;
    const row = rows.find((r) => r.user.id === uid);
    if (!row) return;
    const alvos = row.cells.filter((c) => ehSelecionavel(c.estado)).map((c) => c.date);
    if (alvos.length === 0) return;
    setSelecao((anterior) => {
      const atual = anterior[uid] || {};
      const todosMarcados = alvos.every((d) => atual[d]);
      const proximo = { ...atual };
      alvos.forEach((d) => {
        if (todosMarcados) delete proximo[d];
        else proximo[d] = proximo[d] || turnoPadrao;
      });
      return { ...anterior, [uid]: proximo };
    });
  };

  const alternarColuna = (date) => {
    const elegiveis = linhasListadas.filter((row) =>
      row.cells.some((c) => c.date === date && ehSelecionavel(c.estado))
    );
    if (elegiveis.length === 0) return;
    setSelecao((anterior) => {
      const todosMarcados = elegiveis.every((row) => anterior[row.user.id]?.[date]);
      const proximo = { ...anterior };
      elegiveis.forEach((row) => {
        const atual = { ...(proximo[row.user.id] || {}) };
        if (todosMarcados) delete atual[date];
        else atual[date] = atual[date] || turnoPadrao;
        proximo[row.user.id] = atual;
      });
      return proximo;
    });
  };

  const aoPressionarGrade = (evento) => {
    if (evento.button !== 0) return;
    // O campo de horas mora dentro da coluna do militar, que também alterna a
    // linha: clicar nele não pode pintar 22 dias.
    if (evento.target.closest(SELETOR_CAMPO)) return;
    const celula = evento.target.closest(SELETOR_MARCAVEL);
    if (celula) {
      evento.preventDefault(); // sem isso o arrasto vira seleção de texto
      const marcar = celula.dataset.sel !== '1';
      arrasto.current = { ativo: true, marcar };
      // O preventDefault acima também barra o foco nativo do clique. Sem
      // devolver o foco na mão, o activeElement fica no <body> e quem clica
      // numa célula e tenta seguir com as setas não consegue — o teclado só
      // funcionaria depois de um Tab. focarCelula move o roving tabIndex E o
      // foco do DOM.
      focarCelula(celula.dataset.uid, celula.dataset.date);
      linhaTocada.current = celula.dataset.uid;
      pintarCelula(celula.dataset.uid, celula.dataset.date, marcar);
      return;
    }
    // Clique numa célula bloqueada não pinta nada, mas leva o foco junto: as
    // setas seguem daí, e não de onde o teclado tinha parado antes.
    const bloqueada = evento.target.closest(SELETOR_CELULA);
    if (bloqueada) {
      focarCelula(bloqueada.dataset.uid, bloqueada.dataset.date);
      return;
    }
    const linha = evento.target.closest('[data-rowtoggle]');
    if (linha) {
      alternarLinha(linha.dataset.rowtoggle);
      return;
    }
    const coluna = evento.target.closest('[data-coltoggle]');
    if (coluna) alternarColuna(coluna.dataset.coltoggle);
  };

  const aoArrastarGrade = (evento) => {
    if (!arrasto.current.ativo) return;
    const celula = evento.target.closest(SELETOR_MARCAVEL);
    if (!celula) return;
    linhaTocada.current = celula.dataset.uid;
    pintarCelula(celula.dataset.uid, celula.dataset.date, arrasto.current.marcar);
  };

  /* ------------------------ navegação por teclado ------------------------ */

  // O focus() nativo rola o mínimo possível — e o mínimo deixa a célula
  // escondida atrás da coluna do militar, da coluna de total ou do cabeçalho
  // de dias, todos sticky. As larguras são medidas porque mudam no responsivo.
  const manterVisivel = (celula) => {
    const wrap = gradeRef.current;
    if (!wrap) return;
    const linha = celula.parentElement;
    const margemEsq = linha?.querySelector('.bulk-name')?.offsetWidth || 0;
    const margemDir = linha?.querySelector('.bulk-total')?.offsetWidth || 0;
    const margemTopo = wrap.querySelector('thead')?.offsetHeight || 0;
    const alvo = celula.getBoundingClientRect();
    const area = wrap.getBoundingClientRect();

    if (alvo.left < area.left + margemEsq) wrap.scrollLeft -= area.left + margemEsq - alvo.left;
    else if (alvo.right > area.right - margemDir) {
      wrap.scrollLeft += alvo.right - (area.right - margemDir);
    }
    if (alvo.top < area.top + margemTopo) wrap.scrollTop -= area.top + margemTopo - alvo.top;
    else if (alvo.bottom > area.bottom) wrap.scrollTop += alvo.bottom - area.bottom;
  };

  const focarCelula = (uid, date) => {
    setFoco({ uid, date });
    // A célula já existe no DOM; só o tabIndex muda no próximo render, e
    // trocar o tabIndex de um elemento focado não tira o foco dele.
    const alvo = gradeRef.current?.querySelector(`[data-uid="${uid}"][data-date="${date}"]`);
    if (!alvo) return;
    alvo.focus({ preventScroll: true });
    manterVisivel(alvo);
  };

  const aoTeclarGrade = (evento) => {
    const celula = evento.target.closest(SELETOR_CELULA);
    if (!celula) return;
    const { uid, date } = celula.dataset;
    const linhaAtual = linhasPagina.findIndex((r) => r.user.id === uid);
    const colunaAtual = dias.findIndex((d) => d.date === date);
    if (linhaAtual < 0 || colunaAtual < 0) return;

    const ultimaLinha = linhasPagina.length - 1;
    const ultimaColuna = dias.length - 1;
    const comando = evento.ctrlKey || evento.metaKey;
    let linha = linhaAtual;
    let coluna = colunaAtual;

    switch (evento.key) {
      case 'ArrowLeft':
        // Na primeira coluna a seta continua na linha de cima, no último dia —
        // percorrer a grade inteira não exige voltar ao mouse.
        if (colunaAtual > 0) coluna = colunaAtual - 1;
        else if (linhaAtual > 0) {
          linha = linhaAtual - 1;
          coluna = ultimaColuna;
        }
        break;
      case 'ArrowRight':
        if (colunaAtual < ultimaColuna) coluna = colunaAtual + 1;
        else if (linhaAtual < ultimaLinha) {
          linha = linhaAtual + 1;
          coluna = 0;
        }
        break;
      case 'ArrowUp':
        linha = Math.max(0, linhaAtual - 1);
        break;
      case 'ArrowDown':
        linha = Math.min(ultimaLinha, linhaAtual + 1);
        break;
      case 'Home':
        coluna = 0;
        if (comando) linha = 0;
        break;
      case 'End':
        coluna = ultimaColuna;
        if (comando) linha = ultimaLinha;
        break;
      case ' ':
      case 'Enter':
        evento.preventDefault(); // Espaço rolaria a página
        if (comando) alternarColuna(date);
        else if (evento.shiftKey) alternarLinha(uid);
        else alternarCelula(uid, date);
        return;
      default:
        return;
    }

    evento.preventDefault(); // as setas rolariam o container em vez de andar
    if (linha === linhaAtual && coluna === colunaAtual) return;
    const destinoUid = linhasPagina[linha].user.id;
    const destinoDate = dias[coluna].date;
    focarCelula(destinoUid, destinoDate);
    // Shift+seta é o arrasto do teclado: anda pintando célula a célula. Só nas
    // setas — Shift+End pularia um trecho inteiro sem pintar o caminho.
    if (evento.shiftKey && evento.key.startsWith('Arrow')) {
      alternarCelula(destinoUid, destinoDate);
    }
  };

  /* ------------------------ preenchimento automático ------------------------ */

  /**
   * Fecha a cota do ciclo de cada militar listado: dias cheios enquanto o
   * horário do lote couber e, se sobrar um resto (44h de 48h = 4h), um último
   * dia curto começando na mesma hora — 08:00→12:00.
   */
  const preencherAteCota = () => {
    if (horasPorDia <= 0) {
      toast.error('Defina um horário válido antes de preencher');
      return;
    }
    const proximo = { ...selecao };
    let escolhidos = 0;
    const naoCoube = [];
    linhasListadas.forEach((row) => {
      const cota = Number(row.user.cota) || 0;
      // COMPLETA a seleção manual, não a substitui: quem já pintou dias à mão
      // (inclusive fins de semana) perderia o trabalho se isto sobrescrevesse.
      const jaMarcados = { ...(selecao[row.user.id] || {}) };
      const restante = cota - (Number(row.user.horasNoPeriodo) || 0) - horasDe(jaMarcados);
      if (cota <= 0 || restante < SOBRA_MIN - EPS) return;
      const livres = row.uteisLivres.filter((d) => !jaMarcados[d]);
      const { marcados, sobra } = distribuirEmDias(livres, restante, turnoPadrao, horasPorDia);
      proximo[row.user.id] = { ...jaMarcados, ...marcados };
      escolhidos += diasDe(marcados);
      if (sobra > SOBRA_MIN - EPS) {
        naoCoube.push({ id: row.user.id, nome: row.user.name, sobra });
      }
    });
    setSelecao(proximo);
    setSobras(naoCoube);
    if (escolhidos === 0) {
      toast('Nenhum dia útil livre para preencher', { icon: 'ℹ️' });
      return;
    }
    toast.success(
      `${escolhidos} dia(s) selecionados` +
        (naoCoube.length > 0 ? ` · ${naoCoube.length} militar(es) sem dias úteis suficientes` : '')
    );
  };

  /**
   * Aba dos instrutores: espalha as horas pedidas de cada linha listada em
   * blocos do tamanho escolhido, o último menor para fechar a conta.
   */
  const distribuirHoras = () => {
    if (horasPorDia <= 0) {
      toast.error('O bloco escolhido ultrapassa a meia-noite — ajuste a hora de início');
      return;
    }
    // Pedir mais do que o teto do ciclo não trava o lançamento — o excedente
    // fica retido no registro, depois e em silêncio. Avisa antes de espalhar
    // os dias, contra o TETO da pessoa (não a cota, que é a carga pedida).
    const acimaDoTeto = linhasListadas.filter((row) => {
      const teto = Number(row.user.teto) || Number(row.user.cota) || 0;
      const pedido = pedidoDe(row);
      return (
        teto > 0 && pedido > 0 && (Number(row.user.horasNoPeriodo) || 0) + pedido - teto > EPS
      );
    });
    if (acimaDoTeto.length > 0) {
      const nomes = acimaDoTeto.slice(0, 3).map((r) => r.user.name.split(' ')[0]);
      toast(
        `${acimaDoTeto.length} militar(es) passam do teto do ciclo (${nomes.join(', ')}` +
          `${acimaDoTeto.length > nomes.length ? '…' : ''}) — o excedente fica retido no registro`,
        { icon: '⚠️', duration: 6000 }
      );
    }

    const proximo = { ...selecao };
    let escolhidos = 0;
    let atendidos = 0;
    const naoCoube = [];
    linhasListadas.forEach((row) => {
      const pedido = pedidoDe(row);
      const jaMarcados = { ...(selecao[row.user.id] || {}) };
      const restante = pedido - horasDe(jaMarcados);
      if (pedido <= 0 || restante < SOBRA_MIN - EPS) return;
      atendidos += 1;
      const livres = row.uteisLivres.filter((d) => !jaMarcados[d]);
      const { marcados, sobra } = distribuirEmDias(livres, restante, turnoPadrao, horasPorDia);
      proximo[row.user.id] = { ...jaMarcados, ...marcados };
      escolhidos += diasDe(marcados);
      if (sobra > SOBRA_MIN - EPS) {
        naoCoube.push({ id: row.user.id, nome: row.user.name, sobra });
      }
    });
    setSelecao(proximo);
    setSobras(naoCoube);
    if (atendidos === 0) {
      toast('Nenhum militar listado com horas a lançar', { icon: 'ℹ️' });
      return;
    }
    if (escolhidos === 0) {
      toast('Nenhum dia útil livre para distribuir', { icon: 'ℹ️' });
      return;
    }
    toast.success(
      `${escolhidos} dia(s) marcados em ${atendidos} militar(es)` +
        (naoCoube.length > 0 ? ` · ${naoCoube.length} não coube(ram) por inteiro` : '')
    );
  };

  const limparSelecao = () => {
    setSelecao({});
    setSobras([]);
  };

  /* ------------------------ lançamento ------------------------ */

  const itens = useMemo(() => {
    const lista = [];
    rows.forEach((row) => {
      const marcados = selecao[row.user.id];
      if (!marcados) return;
      Object.keys(marcados)
        .sort()
        .forEach((date) =>
          lista.push({
            userId: row.user.id,
            date,
            // O horário vai POR ITEM: é assim que o dia curto que fecha a cota
            // entra com menos horas que o padrão do lote.
            entryTime: marcados[date].entryTime,
            exitTime: marcados[date].exitTime,
            ...(row.user.positionId ? { positionId: row.user.positionId } : {}),
          })
        );
    });
    return lista;
  }, [rows, selecao]);

  const itensParciais = useMemo(
    () =>
      itens.filter(
        (i) => Math.abs(horasDoTurno(i) - horasPorDia) > EPS
      ).length,
    [itens, horasPorDia]
  );

  const militaresNoLote = useMemo(
    () => new Set(itens.map((i) => i.userId)).size,
    [itens]
  );

  const lancar = async () => {
    setLancando(true);
    try {
      const { data: resposta } = await api.post('/time-records/bulk', {
        items: itens,
        entryTime: turnoPadrao.entryTime,
        exitTime: turnoPadrao.exitTime,
        ...(observacao.trim() ? { observation: observacao.trim() } : {}),
      });
      setConfirmando(false);
      setResultado(resposta);
      setObservacao('');

      // A seleção só é limpa do que REALMENTE entrou. O que voltou em
      // `ignorados` (impedimento, erro) continua pintado: é a única forma de
      // o admin ver o que ficou de fora depois de fechar o modal — a lista
      // do resultado some com um clique.
      const criados = new Set(
        (resposta?.criados || []).map((c) => `${c.userId}|${c.date}`)
      );
      setSelecao((atual) => {
        const proximo = {};
        Object.entries(atual).forEach(([userId, mapa]) => {
          const restantes = {};
          Object.entries(mapa).forEach(([date, turno]) => {
            if (!criados.has(`${userId}|${date}`)) restantes[date] = turno;
          });
          if (diasDe(restantes) > 0) proximo[userId] = restantes;
        });
        return proximo;
      });

      if (resposta?.resumo?.criados > 0) {
        toast.success(`${resposta.resumo.criados} registro(s) lançado(s)`);
      }
      if (resposta?.resumo?.ignorados > 0) {
        toast(
          `${resposta.resumo.ignorados} dia(s) não entraram e seguem marcados na grade`,
          { icon: '⚠️', duration: 6000 }
        );
      }
      await carregar('silent');
    } catch (erro) {
      toast.error(mensagemErro(erro, 'Falha ao lançar os registros'));
    } finally {
      setLancando(false);
    }
  };

  const nomePorId = useMemo(() => {
    const mapa = new Map();
    (dados?.usuarios || []).forEach((u) => mapa.set(u.id, u.name));
    rows.forEach((r) => mapa.set(r.user.id, r.user.name));
    return mapa;
  }, [dados, rows]);

  const trocarCiclo = (modo) => {
    setModoPeriodo(modo);
    if (modo === 'atual' || modo === 'anterior') {
      const ciclo = cicloDe(new Date(), modo === 'anterior' ? -1 : 0);
      setInicio(ciclo.start);
      setFim(ciclo.end);
    }
  };

  const escolherInicio = (data) => {
    if (!data) return;
    const iso = toISO(data);
    setInicio(iso);
    if (iso > fim) setFim(iso);
  };

  const escolherFim = (data) => {
    if (!data) return;
    const iso = toISO(data);
    setFim(iso);
    if (iso < inicio) setInicio(iso);
  };

  const horarioValido = horasPorDia > 0;
  const podeLancar = itens.length > 0 && horarioValido && !lancando;

  /* ------------------------ render ------------------------ */

  const abas = (
    <div className="bulk-tabs" role="group" aria-label="Público do lançamento">
      {ABAS.map((opcao) => {
        const ativa = aba === opcao.id;
        return (
          <button
            key={opcao.id}
            type="button"
            className={`bulk-tab ${ativa ? 'is-on' : ''}`}
            aria-pressed={ativa}
            onClick={() => setAba(opcao.id)}
          >
            <span className="bulk-tab-icon">
              {opcao.id === 'cmcb' ? (
                <PersonIcon width={16} height={16} />
              ) : (
                <ClockIcon width={16} height={16} />
              )}
            </span>
            <span className="bulk-tab-body">
              <span className="bulk-tab-label">{opcao.label}</span>
              <span className="bulk-tab-hint">
                {opcao.id === 'cmcb' ? `teto de ${tetoColegio}h no ciclo` : opcao.hint}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );

  if (loading) {
    return (
      <>
        <Toaster position="top-right" />
        <Navbar />
        <div className="container">
          <div className="bulk-skel bulk-skel-header" />
          {abas}
          <div className="bulk-skel bulk-skel-toolbar" />
          <div className="bulk-skel-kpis">
            <div className="bulk-skel bulk-skel-kpi" />
            <div className="bulk-skel bulk-skel-kpi" />
            <div className="bulk-skel bulk-skel-kpi" />
            <div className="bulk-skel bulk-skel-kpi" />
          </div>
          <div className="bulk-skel bulk-skel-grid" />
          <div className="loading-container" style={{ minHeight: 'auto', padding: '24px 0' }}>
            <div className="loading-spinner" />
            <p>Montando a grade do período...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Toaster position="top-right" />
      <Navbar />
      <div className="container bulk-container">
        <div className="page-header-new">
          <div>
            <h1>Lançamento em massa</h1>
            <p className="subtitle">
              {ehInstrutores
                ? 'Distribua as horas-aula dos instrutores em blocos e lance o ciclo inteiro de uma vez.'
                : 'Feche o ciclo do efetivo lotado no CMCB de uma vez: marque os dias na grade e lance tudo em um clique.'}
            </p>
          </div>
          <div className="header-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => carregar('check')}
              disabled={verificando || rows.length === 0}
              title="Reconsulta impedimentos e registros já existentes"
            >
              <UpdateIcon width={15} height={15} />
              {verificando ? 'Verificando...' : 'Verificar'}
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => setConfirmando(true)}
              disabled={!podeLancar}
            >
              <LayersIcon width={15} height={15} />
              {itens.length > 0 ? `Lançar ${itens.length} registros` : 'Lançar registros'}
            </button>
          </div>
        </div>

        {abas}

        {rows.length === 0 && erroCarga ? (
          <div className="bulk-empty">
            <span className="bulk-empty-icon is-alert">
              <ExclamationTriangleIcon width={30} height={30} />
            </span>
            <h2 className="bulk-empty-title">Não foi possível carregar a grade</h2>
            <p className="bulk-empty-text">{erroCarga}</p>
            <button
              type="button"
              className="btn-primary bulk-empty-cta"
              onClick={() => carregar('full')}
            >
              <UpdateIcon width={15} height={15} />
              Tentar novamente
            </button>
          </div>
        ) : rows.length === 0 && ehInstrutores ? (
          <div className="bulk-empty">
            <span className="bulk-empty-icon">
              <ClockIcon width={30} height={30} />
            </span>
            <h2 className="bulk-empty-title">Nenhum instrutor autorizado</h2>
            <p className="bulk-empty-text">
              Esta aba é uma <strong>lista nominal</strong>: só aparece quem está marcado como
              <strong> instrutor com lançamento em massa</strong> no cadastro, e o sistema recusa
              lançar em lote para qualquer outro militar. Marque quem deve entrar na tela de
              Militares.
            </p>
            <Link to="/admin/users" className="btn-primary bulk-empty-cta">
              Ir para Militares
              <ArrowRightIcon width={15} height={15} />
            </Link>
          </div>
        ) : rows.length === 0 ? (
          <div className="bulk-empty">
            <span className="bulk-empty-icon">
              <LayersIcon width={30} height={30} />
            </span>
            <h2 className="bulk-empty-title">
              {efetivoInativos > 0
                ? 'Nenhum militar do CMCB está ativo'
                : 'Nenhum militar marcado como efetivo do CMCB'}
            </h2>
            <p className="bulk-empty-text">
              {efetivoInativos > 0 ? (
                <>
                  Existe(m) <strong>{efetivoInativos}</strong> militar(es) marcado(s) como efetivo do
                  CMCB, mas todos estão inativos. Reative o cadastro na tela de Militares para que
                  apareçam aqui.
                </>
              ) : (
                <>
                  Esta tela trabalha com a lista de militares lotados no CMCB (teto de{' '}
                  {tetoColegio}h por ciclo). A marcação é feita na tela de Militares:
                </>
              )}
            </p>
            {efetivoInativos === 0 && (
              <ol className="bulk-empty-steps">
                <li>
                  Abra o menu <strong>Militares</strong>.
                </li>
                <li>Selecione os militares na lista (seleção em massa).</li>
                <li>
                  Clique em <strong>Marcar como efetivo do CMCB</strong>.
                </li>
              </ol>
            )}
            <Link to="/admin/users" className="btn-primary bulk-empty-cta">
              Ir para Militares
              <ArrowRightIcon width={15} height={15} />
            </Link>
          </div>
        ) : (
          <>
            <section className="bulk-toolbar">
              <div className="bulk-field">
                <span className="bulk-field-label">Período</span>
                <div className="bulk-seg">
                  {[
                    { id: 'atual', label: 'Ciclo atual' },
                    { id: 'anterior', label: 'Ciclo anterior' },
                    { id: 'custom', label: 'Personalizado' },
                  ].map((opcao) => (
                    <button
                      key={opcao.id}
                      type="button"
                      className={`bulk-seg-btn ${modoPeriodo === opcao.id ? 'is-on' : ''}`}
                      onClick={() => trocarCiclo(opcao.id)}
                    >
                      {opcao.label}
                    </button>
                  ))}
                </div>
                {modoPeriodo === 'custom' ? (
                  <div className="bulk-dates">
                    <DatePicker
                      selected={fromISO(inicio)}
                      onChange={escolherInicio}
                      dateFormat="dd/MM/yyyy"
                      locale="pt-BR"
                      className="bulk-date-input"
                    />
                    <span className="bulk-dates-sep">até</span>
                    <DatePicker
                      selected={fromISO(fim)}
                      onChange={escolherFim}
                      dateFormat="dd/MM/yyyy"
                      locale="pt-BR"
                      className="bulk-date-input"
                    />
                  </div>
                ) : (
                  <span className="bulk-field-hint">
                    {formatBR(inicio)} a {formatBR(fim)} · {dias.length} dias
                  </span>
                )}
                {dados?.periodo?.ehCicloCompleto === false && (
                  <span className="bulk-field-warn">
                    A cota é medida no ciclo {formatBR(dados.periodo.ciclo.start)} a{' '}
                    {formatBR(dados.periodo.ciclo.end)} — o total de cada linha inclui o
                    que já foi lançado fora desta janela.
                  </span>
                )}
                {dados?.tetosAtivos === false && (
                  <span className="bulk-field-warn">
                    Tetos desligados na tela Limites: nada fica retido — todas as horas
                    lançadas vão para a CGP, mesmo acima do teto.
                  </span>
                )}
              </div>

              {ehInstrutores ? (
                <div className="bulk-field">
                  <span className="bulk-field-label">Bloco por dia</span>
                  <div className="bulk-times">
                    <div className="bulk-seg">
                      {BLOCOS.map((horas) => (
                        <button
                          key={horas}
                          type="button"
                          className={`bulk-seg-btn ${blocoHoras === horas ? 'is-on' : ''}`}
                          onClick={() => setBlocoHoras(horas)}
                        >
                          {horas}h
                        </button>
                      ))}
                    </div>
                    <span className="bulk-dates-sep">a partir de</span>
                    <input
                      type="time"
                      className="bulk-time-input"
                      value={blocoInicio}
                      onChange={(e) => setBlocoInicio(e.target.value)}
                      aria-label="Hora de início do bloco"
                    />
                  </div>
                  <span className={`bulk-field-hint ${horarioValido ? '' : 'is-alert'}`}>
                    {horarioValido
                      ? `= ${turnoPadrao.entryTime} às ${turnoPadrao.exitTime} por dia cheio`
                      : 'O bloco ultrapassa a meia-noite'}
                  </span>
                </div>
              ) : (
                <div className="bulk-field">
                  <span className="bulk-field-label">Horário padrão do lote</span>
                  <div className="bulk-times">
                    <input
                      type="time"
                      className="bulk-time-input"
                      value={entryTime}
                      onChange={(e) => setEntryTime(e.target.value)}
                      aria-label="Horário de entrada"
                    />
                    <span className="bulk-dates-sep">às</span>
                    <input
                      type="time"
                      className="bulk-time-input"
                      value={exitTime}
                      onChange={(e) => setExitTime(e.target.value)}
                      aria-label="Horário de saída"
                    />
                  </div>
                  <span className={`bulk-field-hint ${horarioValido ? '' : 'is-alert'}`}>
                    {horarioValido
                      ? `= ${formatHoras(horasPorDia)} por dia`
                      : 'A saída precisa ser depois da entrada'}
                  </span>
                </div>
              )}

              <div className="bulk-field bulk-field-grow">
                <span className="bulk-field-label">Buscar militar</span>
                <div className="search-bar">
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Nome, posto ou matrícula..."
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                  />
                </div>
                {ehInstrutores ? (
                  <>
                    <span className="bulk-field-hint">
                      {linhasListadas.length} listado(s) de {instrutoresTotal} instrutor(es)
                      autorizado(s)
                      {!listaCurta && !mostrarTodos && !busca.trim() && linhasListadas.length < rows.length
                        ? ' · busque pelo nome ou matrícula para incluir mais'
                        : ''}
                    </span>
                    {!listaCurta && (
                      <label className="bulk-toggle">
                        <input
                          type="checkbox"
                          checked={mostrarTodos}
                          onChange={(e) => setMostrarTodos(e.target.checked)}
                        />
                        Mostrar todos os {rows.length} instrutores
                      </label>
                    )}
                  </>
                ) : (
                  <span className="bulk-field-hint">
                    {linhasListadas.length} de {rows.length} militares na grade
                  </span>
                )}
              </div>
            </section>

            <section className="bulk-kpis">
              <article className="bulk-kpi">
                <span className="bulk-kpi-icon">
                  <PersonIcon width={16} height={16} />
                </span>
                <span className="bulk-kpi-body">
                  <span className="bulk-kpi-value">{totais.pessoas}</span>
                  <span className="bulk-kpi-label">
                    {ehInstrutores ? 'instrutores listados' : 'militares do CMCB'}
                  </span>
                </span>
              </article>
              <article className="bulk-kpi">
                <span className="bulk-kpi-icon">
                  <ClockIcon width={16} height={16} />
                </span>
                <span className="bulk-kpi-body">
                  <span className="bulk-kpi-value">
                    {formatHoras(ehInstrutores ? totais.pedidas : totais.lancadas)}
                  </span>
                  <span className="bulk-kpi-label">
                    {ehInstrutores ? 'pedidas nos listados' : 'já lançadas no período'}
                  </span>
                </span>
              </article>
              <article className={`bulk-kpi ${totais.diasSelecionados > 0 ? 'is-active' : ''}`}>
                <span className="bulk-kpi-icon">
                  <CheckCircledIcon width={16} height={16} />
                </span>
                <span className="bulk-kpi-body">
                  <span className="bulk-kpi-value">
                    {totais.diasSelecionados}
                    <small> dias</small>
                  </span>
                  <span className="bulk-kpi-label">
                    selecionados = {formatHoras(totais.horasSelecionadas)}
                  </span>
                </span>
              </article>
              <article className={`bulk-kpi ${totais.faltam === 0 ? 'is-done' : 'is-warn'}`}>
                <span className="bulk-kpi-icon">
                  <StopwatchIcon width={16} height={16} />
                </span>
                <span className="bulk-kpi-body">
                  <span className="bulk-kpi-value">{formatHoras(totais.faltam)}</span>
                  <span className="bulk-kpi-label">
                    {totais.faltam === 0
                      ? ehInstrutores
                        ? 'os pedidos estão fechados'
                        : 'todos batem a cota'
                      : ehInstrutores
                      ? 'faltam para fechar os pedidos'
                      : `faltam para todos baterem ${cotaComum ? formatHoras(cotaComum) : 'a cota'}`}
                  </span>
                </span>
              </article>
            </section>

            <div className="bulk-actions">
              <div className="bulk-actions-left">
                {ehInstrutores ? (
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={distribuirHoras}
                    disabled={!horarioValido || linhasListadas.length === 0}
                    title={`Marca os dias úteis livres em blocos de ${blocoHoras}h até fechar as horas pedidas de cada listado`}
                  >
                    <MagicWandIcon width={14} height={14} />
                    Distribuir horas em blocos de {blocoHoras}h
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={preencherAteCota}
                    disabled={!horarioValido}
                    title="Marca os dias úteis livres, em ordem, até completar a cota de cada um — o último dia pode ser mais curto"
                  >
                    <MagicWandIcon width={14} height={14} />
                    Preencher até {cotaComum ? formatHoras(cotaComum) : 'a cota'}
                  </button>
                )}
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={limparSelecao}
                  disabled={totais.diasSelecionados === 0}
                >
                  <EraserIcon width={14} height={14} />
                  Limpar seleção
                </button>
              </div>
              <ul className="bulk-legend">
                <li>
                  <span className="bulk-swatch is-livre" />
                  livre
                </li>
                <li>
                  <span className="bulk-swatch is-selected" />
                  selecionado
                </li>
                <li>
                  <span className="bulk-swatch is-parcial" />
                  dia curto
                </li>
                <li>
                  <span className="bulk-swatch is-lancado" />
                  já lançado
                </li>
                <li>
                  <span className="bulk-swatch is-impedido" />
                  impedido
                </li>
                <li title="A CGP recusou: essas horas não são pagas">
                  <span className="bulk-swatch is-glosado" />
                  glosado pela CGP
                </li>
                <li title="A CGP abriu pendência neste dia — pode virar glosa">
                  <span className="bulk-swatch is-pendente-cgp" />
                  pendência na CGP
                </li>
                <li>
                  <span className="bulk-swatch is-fds" />
                  fim de semana
                </li>
              </ul>
            </div>

            {sobras.length > 0 && (
              <div className="bulk-sobras" role="status">
                <button
                  type="button"
                  className="bulk-sobras-close"
                  onClick={() => setSobras([])}
                  aria-label="Dispensar aviso"
                >
                  ×
                </button>
                <p className="bulk-sobras-titulo">
                  <ExclamationTriangleIcon width={14} height={14} />
                  Faltaram dias úteis livres para {sobras.length} militar(es):
                </p>
                <ul className="bulk-sobras-list">
                  {sobras.map((s) => (
                    <li key={s.id}>
                      <strong>{s.nome}</strong>
                      <span>{formatHoras(s.sobra)} sem onde entrar</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="bulk-hint" id="bulk-grade-ajuda">
              Clique para marcar, arraste para pintar vários dias, clique no dia para alternar a
              coluna e no nome para alternar a linha inteira. Pelo teclado: <kbd>Tab</kbd> entra na
              grade, as setas movem, <kbd>Espaço</kbd> marca o dia, <kbd>Shift</kbd> + setas pintam,{' '}
              <kbd>Shift</kbd> + <kbd>Espaço</kbd> alterna a linha e <kbd>Ctrl</kbd> +{' '}
              <kbd>Espaço</kbd> alterna a coluna. Célula com número é dia curto — o horário está no
              tooltip.
            </p>

            {/* mousedown/mouseover/keydown ficam no contêiner: 1 handler para ~1200 células */}
            <div
              className="bulk-grid-wrap"
              ref={gradeRef}
              onMouseDown={aoPressionarGrade}
              onMouseOver={aoArrastarGrade}
              onKeyDown={aoTeclarGrade}
            >
              <table
                className={`bulk-grid ${ehInstrutores ? 'is-instrutores' : ''}`}
                role="grid"
                aria-label="Grade de dias por militar"
                aria-describedby="bulk-grade-ajuda"
                aria-multiselectable="true"
              >
                <thead>
                  <tr role="row" className="bulk-head-months">
                    <th className="bulk-corner" rowSpan={2} scope="col">
                      {ehInstrutores ? 'Militar / horas a lançar' : 'Militar'}
                    </th>
                    {gruposMes.map((grupo) => (
                      <th key={grupo.chave} className="bulk-month" colSpan={grupo.qtd} scope="col">
                        {grupo.label}
                      </th>
                    ))}
                    <th className="bulk-corner-right" rowSpan={2} scope="col">
                      {ehInstrutores ? 'Total / pedido' : 'Total / cota'}
                    </th>
                  </tr>
                  <tr role="row" className="bulk-head-days">
                    {dias.map((d) => (
                      <th
                        key={d.date}
                        scope="col"
                        className={
                          'bulk-day' +
                          (d.isWeekend ? ' is-weekend' : '') +
                          (d.isToday ? ' is-today' : '') +
                          (d.isMonthStart ? ' is-month-start' : '')
                        }
                        data-coltoggle={d.date}
                        title={`${d.legenda} — clique para alternar a coluna`}
                        aria-label={d.porExtenso}
                      >
                        <span className="bulk-day-num">{d.dia}</span>
                        <span className="bulk-day-wd">{d.inicial}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {linhasPagina.map((row) => (
                    <LinhaMilitar
                      key={row.user.id}
                      row={row}
                      tetosAtivos={dados?.tetosAtivos !== false}
                      sel={selecao[row.user.id]}
                      horasPadrao={horasPorDia}
                      meta={metaDe(row)}
                      modoPedido={ehInstrutores}
                      pedido={ehInstrutores ? pedidoDe(row) : 0}
                      pedidoTexto={ehInstrutores ? textoPedido(row) : ''}
                      aoMudarPedido={aoMudarPedido}
                      focoDate={focoAtual?.uid === row.user.id ? focoAtual.date : null}
                      aviso={avisosPorMilitar.get(row.user.id) || null}
                    />
                  ))}
                </tbody>
              </table>

              {linhasListadas.length === 0 && (
                <div className="bulk-nofilter">
                  {busca.trim()
                    ? `Nenhum militar encontrado para "${busca}".`
                    : 'Busque pelo nome ou pela matrícula para trazer instrutores para a grade — ou marque "Mostrar todos".'}
                </div>
              )}
            </div>

            {/* Fora da tela: repete o total da linha recém-mexida para quem não
                enxerga a coluna de total mudar. */}
            <p className="bulk-sr-only" aria-live="polite">
              {anuncio}
            </p>

            <Pagination
              id="bulk-grade"
              page={gradePaginacao.page}
              totalPages={gradePaginacao.totalPages}
              total={gradePaginacao.total}
              from={gradePaginacao.from}
              to={gradePaginacao.to}
              pageSize={gradePaginacao.pageSize}
              pageSizeOptions={[25, 50, 100]}
              onPageChange={gradePaginacao.setPage}
              onPageSizeChange={gradePaginacao.setPageSize}
              itemLabel="militares na grade"
            />
          </>
        )}

        {confirmando && (
          <div className="modal-overlay" onClick={() => (lancando ? null : setConfirmando(false))}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Confirmar lançamento</h2>
                <button
                  type="button"
                  className="modal-close"
                  onClick={() => setConfirmando(false)}
                  disabled={lancando}
                >
                  ×
                </button>
              </div>

              <div className="confirmation-body">
                <p>
                  Serão criados <strong>{itens.length} registros</strong> para{' '}
                  <strong>{militaresNoLote} militar(es)</strong>, totalizando{' '}
                  <strong>{formatHoras(totais.horasSelecionadas)}</strong>.
                </p>
                <p>
                  Período de <strong>{formatBR(inicio)}</strong> a <strong>{formatBR(fim)}</strong>,
                  com horário padrão de <strong>{turnoPadrao.entryTime}</strong> às{' '}
                  <strong>{turnoPadrao.exitTime}</strong> ({formatHoras(horasPorDia)} por dia).
                </p>
                {itensParciais > 0 && (
                  <p>
                    <strong>{itensParciais} dia(s)</strong> entram com turno mais curto, para fechar
                    a conta exata sem passar do teto.
                  </p>
                )}
                <p className="warning-text">
                  Dias já lançados ou com impedimento detectado na hora da gravação são ignorados e
                  aparecem no relatório final.
                </p>

                <div className="form-group bulk-obs">
                  <label htmlFor="bulk-observacao">Observação (opcional)</label>
                  <input
                    id="bulk-observacao"
                    type="text"
                    value={observacao}
                    onChange={(e) => setObservacao(e.target.value)}
                    placeholder="Ex.: fechamento do ciclo do CMCB"
                    maxLength={200}
                    disabled={lancando}
                  />
                  <small className="form-hint">Aplicada a todos os registros do lote.</small>
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setConfirmando(false)}
                  disabled={lancando}
                >
                  Cancelar
                </button>
                <button type="button" className="btn-primary" onClick={lancar} disabled={lancando}>
                  {lancando ? 'Lançando...' : `Lançar ${itens.length} registros`}
                </button>
              </div>
            </div>
          </div>
        )}

        {resultado && (
          <div className="modal-overlay" onClick={() => setResultado(null)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Resultado do lançamento</h2>
                <button type="button" className="modal-close" onClick={() => setResultado(null)}>
                  ×
                </button>
              </div>

              <div className="confirmation-body">
                <div className="bulk-result-stats">
                  <div className="bulk-result-stat is-ok">
                    <span className="bulk-result-value">{resultado.resumo?.criados ?? 0}</span>
                    <span className="bulk-result-label">criados</span>
                  </div>
                  <div
                    className={`bulk-result-stat ${
                      resultado.resumo?.ignorados > 0 ? 'is-warn' : ''
                    }`}
                  >
                    <span className="bulk-result-value">{resultado.resumo?.ignorados ?? 0}</span>
                    <span className="bulk-result-label">ignorados</span>
                  </div>
                  <div className="bulk-result-stat">
                    <span className="bulk-result-value">
                      {formatHoras(resultado.resumo?.horas ?? 0)}
                    </span>
                    <span className="bulk-result-label">lançadas</span>
                  </div>
                </div>

                {resultado.ignorados?.length > 0 ? (
                  <>
                    <p className="bulk-result-titulo">
                      <ExclamationTriangleIcon width={14} height={14} />
                      Não foi possível lançar {resultado.ignorados.length} dia(s):
                    </p>
                    <ul className="bulk-result-list">
                      {resultado.ignorados.map((item) => (
                        <li key={`${item.userId}-${item.date}`}>
                          <span className="bulk-result-item-head">
                            <strong>{nomePorId.get(item.userId) || 'Militar'}</strong>
                            <span className="bulk-result-item-date">{formatBR(item.date)}</span>
                            <span className={`badge badge-${item.motivo === 'duplicado' ? 'warning' : 'danger'}`}>
                              {MOTIVOS[item.motivo] || item.motivo}
                            </span>
                          </span>
                          <span className="bulk-result-item-msg">{item.message}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <p className="bulk-result-ok">
                    <CheckCircledIcon width={16} height={16} />
                    Todos os registros selecionados foram lançados.
                  </p>
                )}
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-primary" onClick={() => setResultado(null)}>
                  Fechar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default BulkTimeRecords;
